import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  AppBar,
  Button,
  Grid,
  InputAdornment,
  InputBase,
  inputBaseClasses,
  Paper,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Toolbar,
  Typography,
} from '@mui/material';
import usePagination from '@mui/material/usePagination/usePagination';
import { DocumentDownload, Eye, Magnifier } from 'asset';
import {
  DeleteDialog,
  FilterMenu,
  HeadTable,
  Layout,
  PermissionButton,
  PermissionWrapper,
  TableCaption,
} from 'components';
import { ButtonEdit } from 'components/common/table';
import { GetOptionsResponse } from 'components/form-control/Select';
import PopupEditBaseRAC from 'components/Popups/AC/PopupEditBaseRAC';
import PopupImportCSV from 'components/Popups/PopupImportFile';
import { drawerWidth } from 'config';
import FileSaver from 'file-saver';
import { convertTimeToLocal, getSortItem, NotifyService } from 'helpers';
import { useDateTime } from 'hooks';
import useDebounce from 'hooks/useDebounce';
import {
  FilterOption,
  FilterOptionType,
  FilterValue,
  getKeys,
  OptionItem,
  TYPE_POPUP,
} from 'models';
import {
  AC_BASE_REQUEST_STATUS,
  SUBSCRIPTION_PARAMS_REQUEST,
  SUBSCRIPTION_TYPE,
} from 'models/subscription';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getSupscriptionExportCSVApi,
  importSubscriptionCSVApi,
} from 'services/subscription';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setSubscriptionListParams } from 'store/reducers/subscription';
import { getSubscriptionListRequest } from 'store/reducers/subscription/actionType';
import { theme } from 'theme';
import { headCells, optionStatus, statusColor, statusLabel } from './data';

const filterOptions: FilterOption[] = [
  {
    name: 'Status',
    key: 'status',
    type: FilterOptionType.SINGLE_SELECT,
  },
];

const Subscription = () => {
  const dispatch = useAppDispatch();
  const { formatDateTime } = useDateTime();
  const { subscription_list } = useAppSelector(
    (state) => state.subscriptionReducer.subscription
  );
  const { params } = subscription_list;

  const [selected, setSelected] = useState<readonly string[]>([]);
  const [keyword, setKeyword] = useState<string>('');
  const [openEditPopup, setOpenEditPopup] = useState<boolean>(false);
  const [openImportPopup, setOpenImportPopup] = useState<boolean>(false);

  const [openDeletePopup, setOpenDeletePopup] = useState<boolean>(false);
  const [itemSubscriptions, setItemSubscriptions] =
    useState<SUBSCRIPTION_TYPE>();
  const [isImporting, setIsImporting] = useState<boolean>(false);

  const onSetListParams = (params: SUBSCRIPTION_PARAMS_REQUEST | null) => {
    dispatch(setSubscriptionListParams(params));
  };

  useEffect(() => {
    if (params) {
      dispatch(getSubscriptionListRequest(params));
    }
  }, [params, dispatch]);

  useEffect(() => {
    setKeyword(params?.search ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setOpenEdit = (event: any, data: SUBSCRIPTION_TYPE) => {
    event.stopPropagation();
    setItemSubscriptions(data);
    setOpenEditPopup(true);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent) => {
    const limit = parseInt(event.target.value, 10);
    onSetListParams({ ...params, page: 1, limit });
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const { items } = usePagination({
    count: subscription_list.metadata.total_pages,
    page: subscription_list.metadata.page,
    onChange(_event, page) {
      onSetListParams({ ...params, page: page });
    },
  });

  const onSetKeywordParams = useDebounce((keyword: string) => {
    const _params = { ...params, page: 1 };
    if (keyword) _params.search = keyword;
    else delete _params.search;
    onSetListParams(_params);
  }, 250);
  const onOpenImportSubPopup = () => {
    setOpenImportPopup(true);
  };
  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
    onSetKeywordParams(event.target.value);
  };

  const onClosePopup = (type: number) => {
    switch (type) {
      case TYPE_POPUP.DELETE:
        setOpenDeletePopup(false);
        setItemSubscriptions(undefined);
        break;
      case TYPE_POPUP.EDIT:
        setOpenEditPopup(false);
        setItemSubscriptions(undefined);
        break;
    }
  };

  const onExportCSV = async () => {
    await getSupscriptionExportCSVApi({
      sort: params?.sort,
      ...params,
    })
      .then((res) => {
        FileSaver.saveAs(
          res.data,
          `${moment().format('YYYY-MM-DD')} Subscription.csv`
        );
      })
      .catch((e) => NotifyService.error(e));
  };

  const onReloadList = () => {
    if (!params) return;
    dispatch(getSubscriptionListRequest(params));
  };

  const filterValue: FilterValue = useMemo(() => {
    return {
      status: optionStatus.find(
        (item) => item.id === subscription_list.params?.status
      ),
    };
  }, [subscription_list.params]);

  const fetchDataOption = async (
    key: string,
    keyword?: string,
    limit: number = 20,
    page: number = 1
  ) => {
    let data: GetOptionsResponse | null = null;
    let subStatuses: OptionItem[] = getKeys(AC_BASE_REQUEST_STATUS).map(
      (t) => ({
        id: AC_BASE_REQUEST_STATUS[t],
        label: statusLabel[t.toLocaleLowerCase() as AC_BASE_REQUEST_STATUS],
        color: statusColor[t.toLocaleLowerCase() as AC_BASE_REQUEST_STATUS],
      })
    );

    if (keyword) {
      subStatuses = subStatuses.filter((t) =>
        t.label?.toLocaleLowerCase().includes(keyword.toLocaleLowerCase())
      );
    }
    data = {
      metadata: {
        limit: limit,
        page: page,
        total_pages: 1,
        page_count: subStatuses.length,
        total_count: subStatuses.length,
      },
      options: subStatuses,
    };
    return data;
  };

  const onChangeFilter = (value: FilterValue) => {
    setSelected([]);
    const _params = { ...params };
    delete _params.status;
    onSetListParams({
      ..._params,
      status: (value.status as OptionItem<any>)?.id,
      page: 1,
    });
  };

  const sortItem = useMemo(() => {
    return getSortItem(subscription_list.params?.sort);
  }, [subscription_list.params]);

  const handleRequestSort = (
    _: React.MouseEvent<unknown>,
    property: keyof SUBSCRIPTION_TYPE
  ) => {
    setSelected([]);
    const isAsc = sortItem?.orderBy === property && sortItem?.order === 'asc';
    onSetListParams({
      ...params,
      sort: `${property}-${isAsc ? 'desc' : 'asc'}`,
    });
  };

  const handleImportCSV = async (file: File) => {
    if (!file) return;

    setIsImporting(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await importSubscriptionCSVApi(formData);

      if (response.data.data.failed > 0) {
        const errors = response.data.data.errors || [];

        const uniqueMessages = [
          ...new Set(errors.map((e: { message: string }) => e.message)),
        ];

        const message = uniqueMessages.length
          ? `${response.data.message},  Errors: ${uniqueMessages.join(', ')}`
          : response.data.message || 'Import failed.';

        NotifyService.error(message);
      } else {
        NotifyService.success('CSV file imported successfully.');
        onReloadList();
      }
    } catch (error) {
      NotifyService.error(error);
    } finally {
      setIsImporting(false);
    }
  };
  return (
    <Layout>
      <AppBar
        position="fixed"
        sx={{
          width: `calc(100% - ${drawerWidth}px)`,
          ml: `${drawerWidth}px`,
          backgroundColor: 'white.light',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '16px',
          }}
        >
          <Grid>
            <Typography
              sx={{
                fontSize: '20px',
                lineHeight: 1,
                color: 'grey1.light',
              }}
            >
              Concierge / Subscription
            </Typography>
          </Grid>
          <Grid display="flex" gap="8px" flexGrow={1} justifyContent="center">
            <InputBase
              placeholder="Search"
              sx={{
                maxWidth: '369px',
                flexGrow: 1,
                background: '#F2F4F6',
                border: '1px solid #D9D9D9',
                p: '0px 7px 0px 11px',
                borderRadius: '5px',
                [`& .${inputBaseClasses.input}`]: {
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: '100%',
                  p: 0,
                  height: 'fit-content',
                },
              }}
              onChange={onChangeSearch}
              value={keyword}
              endAdornment={
                <InputAdornment position="end">
                  <Magnifier />
                </InputAdornment>
              }
            />
            <FilterMenu
              filterOptions={filterOptions}
              filterValue={filterValue}
              onChange={onChangeFilter}
              fetchDataOption={fetchDataOption}
            />
          </Grid>
          <Stack direction="row" spacing={2}>
            <PermissionButton
              allowedRoles={['superadmin', 'staff']}
              sx={{
                width: '131px',
                height: 30,
                fontWeight: 600,
                fontSize: '13px',
                border: `1px solid ${theme.palette.secondary.main}`,
                p: 0,
              }}
              variant="outlined"
              color="secondary"
              endIcon={<DocumentDownload />}
              onClick={onExportCSV}
            >
              Export to CSV
            </PermissionButton>

            <PermissionWrapper allowedRoles={['superadmin', 'staff']}>
              <Button
                sx={{
                  width: '131px',
                  height: 30,
                  fontWeight: 600,
                  fontSize: '13px',
                  border: `1px solid ${theme.palette.secondary.main}`,
                  p: 0,
                }}
                variant="outlined"
                color="secondary"
                endIcon={<CloudUploadIcon />}
                onClick={onOpenImportSubPopup}
                disabled={isImporting}
              >
                {isImporting ? 'Importing CSV...' : 'Import CSV'}
              </Button>
            </PermissionWrapper>
          </Stack>
        </Toolbar>
      </AppBar>
      <Stack spacing="8px">
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid #D9D9D9',
            maxHeight: 'calc(100vh - 150px)',
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 650 }}
            aria-labelledby="tableTitle"
          >
            <HeadTable<SUBSCRIPTION_TYPE>
              numSelected={selected.length}
              order={sortItem?.order}
              orderBy={sortItem?.orderBy}
              rowCount={subscription_list.records.length}
              headCells={headCells}
              onCheckbox={false}
              onRequestSort={handleRequestSort}
            />
            <TableBody
              sx={{
                height: 'auto !important',
              }}
            >
              {subscription_list.records.map((row) => {
                const isItemSelected = isSelected(row.id);
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.parent?.subscription?.number}
                    </TableCell>

                    <TableCell
                      component="th"
                      scope="row"
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {`${row?.parent?.User?.first_name} ${row?.parent?.User?.last_name}`}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.start_datetime &&
                        formatDateTime(convertTimeToLocal(row?.start_datetime))}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.end_datetime &&
                        formatDateTime(convertTimeToLocal(row?.end_datetime))}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {`${row?.address?.street}, ${row?.address?.name}`}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      <Grid
                        container
                        alignItems="center"
                        textTransform="capitalize"
                        flexWrap={'nowrap'}
                      >
                        <Grid
                          sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            marginRight: '5px',
                            backgroundColor: statusColor[row.status],
                          }}
                        />
                        <Typography whiteSpace={'nowrap'}>
                          {row.status && statusLabel[row.status]}
                        </Typography>
                      </Grid>
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                        fontSize: '14px',
                        fontWeight: 400,
                      }}
                    >
                      {row?.createdAt &&
                        formatDateTime(convertTimeToLocal(row?.createdAt))}
                    </TableCell>

                    <TableCell
                      align="left"
                      sx={{
                        borderRight: '1px solid #DEE2E6',
                        padding: '18px 12px 17px',
                      }}
                    >
                      <Stack direction="row" spacing="10px">
                        <Link to={`/concierge/subscription/${row.parent_id}`}>
                          <Button
                            sx={{
                              minWidth: '38px !important',
                              height: '32px',
                              '&.MuiButton-root': {
                                p: '0px !important',
                              },
                            }}
                            variant="contained"
                            color="secondary"
                          >
                            <Eye />
                          </Button>
                        </Link>
                        <PermissionWrapper
                          allowedRoles={['superadmin', 'staff']}
                        >
                          <ButtonEdit onClick={(e) => setOpenEdit(e, row)} />
                        </PermissionWrapper>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TableCaption
          rowsPerPage={params?.limit ?? 0}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          metadata={subscription_list.metadata}
          items={items}
        />
      </Stack>
      {openDeletePopup && (
        <DeleteDialog
          name={itemSubscriptions?.id}
          open={openDeletePopup}
          onClose={() => onClosePopup(TYPE_POPUP.DELETE)}
          subject={itemSubscriptions?.id}
          onSuccess={onReloadList}
          type="subscription"
        />
      )}
      {openEditPopup && (
        <PopupEditBaseRAC
          parentId={itemSubscriptions?.parent_id}
          open={openEditPopup}
          subject={itemSubscriptions?.id}
          handleClosePopup={() => onClosePopup(TYPE_POPUP.EDIT)}
          onSuccess={onReloadList}
        />
      )}
      {openImportPopup && (
        <PopupImportCSV
          onClose={() => setOpenImportPopup(false)}
          open={openImportPopup}
          onUpload={handleImportCSV}
        />
      )}
    </Layout>
  );
};

export default Subscription;
